/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { getWifiSetting, setWifiSetting, downloadVideos, clearVideos, isCellular, loadManifest, isVideoCached } from "../lib/media";
import { Button } from "../components/ui/button";
import { getPushState, subscribePush } from "../lib/push";
import { downloadPdf } from "../lib/pdf";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Crown, Video, Download, Trash2, BellRing, Plus, Send, FileDown, ShieldCheck, Loader2, FileText, Lock, Share2, Mail, MessageCircle } from "lucide-react";

const Card = ({ icon: Icon, title, children, testid }) => (
  <div data-testid={testid} className="bg-white rounded-3xl border border-[#E4E0D5] p-5">
    <div className="flex items-center gap-2 mb-3 text-[#0A1426]/70">
      <Icon size={16} /><span className="text-[11px] uppercase tracking-wider font-bold">{title}</span>
    </div>
    {children}
  </div>
);

export function SubscriptionCard() {
  const navigate = useNavigate();
  const { billing } = useApp();
  return (
    <button data-testid="profile-subscription-card" onClick={() => navigate("/app/subscription")}
      className="w-full text-left rounded-3xl p-5 bg-[#0A1426] text-white flex items-center gap-4">
      <Crown size={28} className="text-[#D4AF5A]" />
      <div className="flex-1">
        <div className="text-[11px] uppercase tracking-wider text-white/70 font-bold">Mon forfait</div>
        <div className="font-[Fraunces] text-xl font-bold">{billing?.plan_name || "Gratuit"}</div>
        <div className="text-[12px] text-white/75">{billing?.plan_code === "gratuit" ? "Débloquez vidéos, rappels SMS et téléconsultations" : "Gérer mon abonnement et mes options"}</div>
      </div>
    </button>
  );
}

export function InstallCard() {
  const navigate = useNavigate();
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
  if (standalone) return null;
  return (
    <button data-testid="profile-install-card" onClick={() => navigate("/installer")} className="w-full text-left bg-white rounded-3xl border border-[#E4E0D5] p-4 flex items-center gap-3 hover:border-[#2BA3A0]">
      <span className="w-12 h-12 rounded-2xl bg-[#F5F3EE] grid place-items-center text-[#2BA3A0]"><Download size={22} /></span>
      <div className="flex-1">
        <div className="font-bold text-[#0A1426]">Installer l'application</div>
        <div className="text-[12px] text-[#0A1426]/60">Sur l'écran d'accueil Android ou iPhone — fonctionne hors-ligne</div>
      </div>
    </button>
  );
}

export function VideosCard() {
  const { hasFeature } = useApp();
  const [wifi, setWifi] = useState(getWifiSetting());
  const [cached, setCached] = useState(0);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const m = await loadManifest();
    const vids = m.exercises.map((e) => e.video).filter(Boolean);
    setTotal(vids.length);
    const flags = await Promise.all(vids.map(isVideoCached));
    setCached(flags.filter(Boolean).length);
  };
  useEffect(() => { refresh(); }, []);

  const download = async () => {
    if (isCellular()) { toast.error("Connexion mobile détectée : téléchargement uniquement en Wi-Fi."); return; }
    setBusy(true);
    await downloadVideos((d, n) => setCached(d));
    setBusy(false);
    toast.success("Vidéos disponibles hors-ligne");
    refresh();
  };

  return (
    <Card icon={Video} title="Vidéos de démonstration" testid="videos-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-bold text-[#0A1426] text-[14px]">Télécharger les vidéos en Wi-Fi</div>
          <div className="text-[11px] text-[#0A1426]/60">Jamais en données mobiles · {cached}/{total} en cache</div>
        </div>
        <Switch data-testid="wifi-videos-switch" checked={wifi} onCheckedChange={(v) => { setWifi(v); setWifiSetting(v); }} />
      </div>
      {!hasFeature("videos") && <p className="text-[11px] text-[#8A6D2B] mt-2">Les vidéos nécessitent le forfait Famille ou supérieur.</p>}
      {wifi && hasFeature("videos") && (
        <div className="flex gap-2 mt-3">
          <Button data-testid="download-videos-btn" disabled={busy} onClick={download} className="flex-1 h-11 rounded-full bg-[#2BA3A0] text-white font-bold">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <><Download size={16} className="mr-2" /> Télécharger</>}
          </Button>
          <Button data-testid="clear-videos-btn" variant="outline" onClick={async () => { await clearVideos(); refresh(); }} className="h-11 w-11 rounded-full border-[#E4E0D5]"><Trash2 size={16} /></Button>
        </div>
      )}
    </Card>
  );
}

export function RemindersCard() {
  const { patient, hasFeature } = useApp();
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [pushState, setPushState] = useState("idle");
  const [form, setForm] = useState({ type: "medicament", message: "", heure: "08:00", canal: "push" });

  const load = () => patient && api.get(`/reminders?patient_id=${patient.id}`).then(({ data }) => setItems(data)).catch(() => null);
  useEffect(() => { load(); }, [patient]); // eslint-disable-line
  useEffect(() => { getPushState().then(setPushState); }, []);

  const enablePush = async () => {
    try { await subscribePush(); setPushState("subscribed"); toast.success("Notifications activées"); await api.post("/push/test"); }
    catch (e) { toast.error(e.message || "Activation impossible"); }
  };

  const add = async () => {
    try {
      await api.post("/reminders", { ...form, patient_id: patient.id });
      setAdding(false); setForm({ ...form, message: "" }); load();
      toast.success("Rappel ajouté");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const sendNow = async (id) => {
    try { const { data } = await api.post(`/reminders/${id}/send-now`); toast.success(data.mode === "simule" ? "SMS simulé (journal)" : "SMS envoyé"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!patient) return null;
  return (
    <Card icon={BellRing} title="Rappels" testid="reminders-card">
      <div data-testid="push-status" className="flex items-center justify-between gap-2 mb-3 bg-[#0A1426] text-white rounded-2xl px-3 py-2">
        <div className="text-[12px]">
          <div className="font-bold">Notifications même app fermée</div>
          <div className="text-white/70">{pushState === "subscribed" ? "Activées sur cet appareil" : pushState === "denied" ? "Bloquées dans le navigateur" : pushState === "unsupported" ? "Non prises en charge ici" : "Rappels envoyés toutes les 15 min à l'heure choisie"}</div>
        </div>
        {pushState === "idle" && <Button data-testid="enable-push-btn" onClick={enablePush} className="h-9 rounded-full bg-[#D4AF5A] text-[#0A1426] font-bold text-[12px] px-3">Activer</Button>}
        {pushState === "subscribed" && <BellRing size={18} className="text-[#D4AF5A]" />}
      </div>
      <div className="flex flex-col gap-2">
        {items.map((r) => (
          <div key={r.id} data-testid={`reminder-${r.id}`} className="flex items-center gap-2 bg-[#F5F3EE] rounded-2xl px-3 py-2">
            <div className="flex-1">
              <div className="text-[13px] font-bold text-[#0A1426]">{r.heure} · {r.type}</div>
              <div className="text-[11px] text-[#0A1426]/70">{r.message} · {r.canal.toUpperCase()}</div>
            </div>
            {r.canal === "sms" && <button data-testid={`reminder-send-${r.id}`} onClick={() => sendNow(r.id)} className="w-9 h-9 grid place-items-center rounded-full bg-white text-[#2BA3A0]"><Send size={14} /></button>}
            <button data-testid={`reminder-del-${r.id}`} onClick={async () => { await api.delete(`/reminders/${r.id}`); load(); }} className="w-9 h-9 grid place-items-center rounded-full bg-white text-[#D9534F]"><Trash2 size={14} /></button>
          </div>
        ))}
        {items.length === 0 && <div className="text-[12px] text-[#0A1426]/60">Aucun rappel.</div>}
      </div>
      {adding ? (
        <div className="mt-3 flex flex-col gap-2">
          <div className="grid grid-cols-2 gap-2">
            <select data-testid="reminder-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="h-11 rounded-full px-3 border border-[#E4E0D5] text-[13px] text-[#0A1426] bg-white">
              <option value="medicament">Médicament</option><option value="exercice">Exercice</option><option value="tension">Tension</option><option value="rendez_vous">Rendez-vous</option>
            </select>
            <input data-testid="reminder-time" type="time" value={form.heure} onChange={(e) => setForm({ ...form, heure: e.target.value })} className="h-11 rounded-full px-3 border border-[#E4E0D5] text-[13px] text-[#0A1426] bg-white" />
          </div>
          <input data-testid="reminder-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Message du rappel" className="h-11 rounded-full px-4 border border-[#E4E0D5] text-[13px] text-[#0A1426] bg-white" />
          <div className="flex gap-2">
            {["push", "sms"].map((c) => (
              <button key={c} data-testid={`reminder-canal-${c}`} onClick={() => setForm({ ...form, canal: c })}
                className={`flex-1 h-10 rounded-full text-[12px] font-bold ${form.canal === c ? "bg-[#0A1426] text-white" : "bg-[#F5F3EE] text-[#0A1426]"}`}>
                {c === "push" ? "Notification" : `SMS${hasFeature("rappels_sms") ? "" : " (Famille)"}`}
              </button>
            ))}
          </div>
          <Button data-testid="reminder-save" onClick={add} disabled={!form.message} className="h-11 rounded-full bg-[#2BA3A0] text-white font-bold">Enregistrer</Button>
        </div>
      ) : (
        <button data-testid="reminder-add-btn" onClick={() => setAdding(true)} className="mt-3 inline-flex items-center gap-2 text-[13px] font-bold text-[#2BA3A0]"><Plus size={16} /> Ajouter un rappel</button>
      )}
    </Card>
  );
}

export function DataCard() {
  const { logout } = useAuth();
  const { patient, hasFeature } = useApp();
  const navigate = useNavigate();
  const [pdfBusy, setPdfBusy] = useState(false);
  const [share, setShare] = useState(null);
  const canPdf = hasFeature("export_pdf");
  const openShare = async () => {
    if (!canPdf) { navigate("/app/subscription"); return; }
    try {
      const { data } = await api.post(`/patients/${patient.id}/share-link`);
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.path}`;
      const text = `Voici mon dossier de suivi Bombacare (tension, médicaments, exercices) — lien valable 7 jours : ${url}`;
      if (navigator.share) {
        try {
          const { data: blob } = await api.get(`/patients/${patient.id}/export.pdf`, { responseType: "blob" });
          const file = new File([blob], "bombacare-dossier.pdf", { type: "application/pdf" });
          if (navigator.canShare?.({ files: [file] })) { await navigator.share({ title: "Mon dossier Bombacare", text, files: [file] }); return; }
          await navigator.share({ title: "Mon dossier Bombacare", text, url }); return;
        } catch (e) { if (e?.name === "AbortError") return; }
      }
      setShare({ url, text });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Partage impossible"); }
  };
  const exportPdf = async () => {
    if (!canPdf) { navigate("/app/subscription"); return; }
    setPdfBusy(true);
    try { await downloadPdf(patient.id, "bombacare-dossier.pdf"); toast.success("Dossier PDF téléchargé"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export impossible"); }
    setPdfBusy(false);
  };
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const exportData = async () => {
    const { data } = await api.get("/me/export");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "bombacare-mes-donnees.json"; a.click();
    toast.success("Export téléchargé");
  };
  const deleteAll = async () => {
    setBusy(true);
    try { await api.delete("/me"); await logout(); navigate("/auth/login", { replace: true }); toast.success("Compte et données supprimés"); }
    catch (e) { toast.error("Suppression impossible"); setBusy(false); }
  };

  return (
    <Card icon={ShieldCheck} title="Mes données (RGPD)" testid="data-card">
      <div className="flex flex-col gap-2">
        {patient && (
          <Button data-testid="export-pdf-btn" disabled={pdfBusy} onClick={exportPdf} className="h-11 rounded-full bg-[#0A1426] text-white font-bold justify-start hover:bg-[#2BA3A0]">
            {pdfBusy ? <Loader2 className="animate-spin mr-2" size={16} /> : canPdf ? <FileText size={16} className="mr-2" /> : <Lock size={16} className="mr-2" />}
            Dossier PDF (courbes & bilans){canPdf ? "" : " — Premium"}
          </Button>
        )}
        {patient && (
          <Button data-testid="share-pdf-btn" onClick={openShare} variant="outline" className="h-11 rounded-full border-[#2BA3A0] text-[#0A1426] font-bold justify-start">
            <Share2 size={16} className="mr-2" /> Partager à un proche{canPdf ? "" : " — Premium"}
          </Button>
        )}
        {share && (
          <div data-testid="share-sheet" className="rounded-2xl bg-[#F5F3EE] p-3 flex flex-col gap-2">
            <a data-testid="share-whatsapp" href={`https://wa.me/?text=${encodeURIComponent(share.text)}`} target="_blank" rel="noreferrer"
              className="h-11 rounded-full bg-[#25D366] text-white font-bold flex items-center justify-center gap-2"><MessageCircle size={16} /> WhatsApp</a>
            <a data-testid="share-email" href={`mailto:?subject=${encodeURIComponent("Mon dossier Bombacare")}&body=${encodeURIComponent(share.text)}`}
              className="h-11 rounded-full bg-[#0A1426] text-white font-bold flex items-center justify-center gap-2"><Mail size={16} /> E-mail</a>
            <button data-testid="share-copy" onClick={() => { navigator.clipboard?.writeText(share.url).catch(() => null); toast.success("Lien copié"); }} className="text-[12px] font-bold text-[#2BA3A0] underline">Copier le lien (7 jours)</button>
          </div>
        )}
        <Button data-testid="export-data-btn" onClick={exportData} variant="outline" className="h-11 rounded-full border-[#E4E0D5] text-[#0A1426] font-bold justify-start"><FileDown size={16} className="mr-2" /> Exporter mes données (JSON)</Button>
        <button data-testid="privacy-link" onClick={() => navigate("/privacy")} className="text-[12px] text-left font-bold text-[#2BA3A0] underline px-1">Politique de confidentialité</button>
        {!confirm ? (
          <button data-testid="delete-data-btn" onClick={() => setConfirm(true)} className="text-[12px] text-left font-bold text-[#D9534F] px-1">Supprimer définitivement mon compte et mes données</button>
        ) : (
          <div className="rounded-2xl bg-[#D9534F]/10 p-3 flex flex-col gap-2">
            <p className="text-[12px] text-[#0A1426]">Cette action est irréversible. Confirmer ?</p>
            <div className="flex gap-2">
              <Button data-testid="delete-confirm-btn" disabled={busy} onClick={deleteAll} className="flex-1 h-10 rounded-full bg-[#D9534F] text-white font-bold">Oui, supprimer</Button>
              <Button data-testid="delete-cancel-btn" onClick={() => setConfirm(false)} variant="outline" className="flex-1 h-10 rounded-full">Annuler</Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
